import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import axios from "axios";
import "./finalPreview.css";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";

interface LocationState {
  draftId: number;
  caption: string;
  images: string[];
}

export default function FinalPreviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!state?.draftId) {
      navigate("/create");
    }
  }, [state, navigate]);

  const postToGelwel = async () => {
    try {
      setLoading(true);
      setError(null);

      const form = new FormData();
      form.append("caption", state!.caption);
      form.append("tags", "");
      form.append("visibility", "public");
      form.append("draft_id", state!.draftId.toString());

      // Only first image for now (extend later)
      const blob = await fetch(state!.images[0]).then(res => res.blob());
      form.append("media", blob, "image.png");

      const res = await axios.post(`${API}/posts/`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess("Posted successfully to Gelwel feed!");
    } catch (e: any) {
      setError(e.response?.data?.detail || "Post failed");
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/post/${state?.draftId}`;
    navigator.clipboard.writeText(link);
    setSuccess("Share link copied to clipboard!");
  };

  const saveForLater = async () => {
    try {
      setLoading(true);
      await axios.post(
        `${API}/drafts/save`,
        { draft_id: state?.draftId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess("Saved for later!");
    } catch (e: any) {
      setError("Could not save for later");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div className="final-root" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="final-shell">

        <h1>Final Preview</h1>
        <p>Review everything before publishing</p>

        {/* IMAGES */}
        <div className="final-image-grid">
          {state?.images.map((img, idx) => (
            <motion.div key={idx} className="final-image-box" whileHover={{ scale: 1.03 }}>
              <img src={img} alt={`final-${idx}`} />
            </motion.div>
          ))}
        </div>

        {/* CAPTION */}
        <div className="final-caption-box">
          <h3>Caption</h3>
          <p>{state?.caption}</p>
        </div>

        {/* ACTIONS */}
        <div className="final-actions">

          <motion.button
            className="final-primary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={postToGelwel}
            disabled={loading}
          >
            {loading ? "Posting..." : "Post to Gelwel"}
          </motion.button>

          <motion.button
            className="final-secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={copyShareLink}
          >
            Copy Share Link
          </motion.button>

          <motion.button
            className="final-secondary"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={saveForLater}
          >
            Save for Later
          </motion.button>
        </div>

        {/* STATUS */}
        <AnimatePresence>
          {error && (
            <motion.div className="final-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div className="final-success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {success}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </motion.div>
  );
}
